/**
 * Hidden staff login (tap the logo 5x on the phone screen): what the sign-in
 * request carries.
 *
 * Pure helpers, no React and no imports, so the unit tests can load this
 * without the React Native runtime.
 *
 * The staff password is shared, so since 2026-10-22 (security plan B2) the
 * operator also gives her own staff email, and the server records it as who
 * used the password (packages/api/src/services/otp.service.ts,
 * resolveStaffOperator). It is a staff account's email, never the nurse's. The
 * app remembers it on this device (STAFF_OPERATOR_EMAIL_KEY) so she types it
 * once; the password itself is never stored.
 */

/** Local storage key for the remembered staff email (lib/local-flags.ts). */
export const STAFF_OPERATOR_EMAIL_KEY = "th-staff-operator-email";

/** Trimmed and lower-cased: how the server matches it. */
export function normalizeOperatorEmail(raw: string | null | undefined): string {
    return (raw ?? "").trim().toLowerCase();
}

export interface OtpRequestBody {
    identifier: string;
    adminPassword?: string;
    operatorEmail?: string;
}

/**
 * The body for POST /auth/otp/request. A nurse signing in sends only her
 * phone. The staff email goes only with a staff password: on its own it means
 * nothing, and a nurse's request never carries one.
 */
export function otpRequestBody(
    identifier: string,
    staffPassword?: string,
    operatorEmail?: string
): OtpRequestBody {
    const password = (staffPassword ?? "").trim();
    if (password === "") {
        return { identifier };
    }
    const email = normalizeOperatorEmail(operatorEmail);
    return email === ""
        ? { identifier, adminPassword: password }
        : { identifier, adminPassword: password, operatorEmail: email };
}
